import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { ArrowLeft } from "@/components/icons";
import {
  Badge,
  Button,
  confirm,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  useCloseEventSale,
  useDeleteEventSaleEvent,
  useEventSaleQuery,
  useOpenEventSale,
} from "../api/queries";
import { EVENT_STATUS_LABELS } from "../lib/labels";
import { EventSaleCatalogPanel } from "../ui/EventSaleCatalogPanel";
import { EventSaleExpensePanel } from "../ui/EventSaleExpensePanel";
import { EventSaleSellPanel } from "../ui/EventSaleSellPanel";
import { EventSaleStockPanel } from "../ui/EventSaleStockPanel";
import { EventSaleSummaryPanel } from "../ui/EventSaleSummaryPanel";

function eventWhen(startsOn: string, endsOn: string | null) {
  if (!endsOn || endsOn === startsOn) return formatDateLocal(startsOn);
  return `${formatDateLocal(startsOn)} – ${formatDateLocal(endsOn)}`;
}

export function EventSaleDetailPage() {
  const { id } = useParams({ from: "/app/event-sales/$id" });
  const nav = useNavigate();
  const canWrite = useActionPermission("event_sales:write");
  const eventQuery = useEventSaleQuery(id);
  const openEvent = useOpenEventSale();
  const closeEvent = useCloseEventSale();
  const removeEvent = useDeleteEventSaleEvent();
  const event = eventQuery.data;
  const locked = event?.status === "closed";
  const editable = canWrite && !locked;
  const [tab, setTab] = useState("sell");
  const [tabReady, setTabReady] = useState(false);

  useEffect(() => {
    if (!event || tabReady) return;
    setTab(event.status === "closed" ? "summary" : "sell");
    setTabReady(true);
  }, [event, tabReady]);

  async function openSale() {
    await openEvent.mutateAsync(id);
    toast.success("ເປີດຂາຍແລ້ວ");
    setTab("sell");
  }

  async function closeSale() {
    const ok = await confirm({
      title: "ປິດງານ?",
      description: "ປິດແລ້ວຂາຍຕໍ່ແລະແກ້ລາຍການບໍ່ໄດ້ ຈົນກວ່າຈະເປີດໃໝ່",
      actionText: "ປິດງານ",
    });
    if (!ok) return;
    await closeEvent.mutateAsync(id);
    toast.success("ປິດງານແລ້ວ");
    setTab("summary");
  }

  async function deleteDraft() {
    const ok = await confirm({
      title: "ລຶບງານນີ້?",
      description: "ລຶບໄດ້ແຕ່ງານຮ່າງທີ່ຍັງບໍ່ມີບິນແລະລາຍຈ່າຍ",
      actionText: "ລຶບ",
    });
    if (!ok) return;
    await removeEvent.mutateAsync(id);
    toast.success("ລຶບງານແລ້ວ");
    void nav({ to: "/app/event-sales" });
  }

  return (
    <>
      <Header>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-11 shrink-0"
            aria-label="ກັບ"
            onClick={() => nav({ to: "/app/event-sales" })}
          >
            <ArrowLeft />
          </Button>
          <h1 className="truncate font-display text-lg">
            {event?.name ?? "ອອກງານ"}
          </h1>
        </div>
      </Header>
      <Main>
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
          {eventQuery.isLoading || !event ? (
            <Skeleton className="h-36 w-full" />
          ) : (
            <>
              <div className="brand-card flex flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {event.eventNumber}
                    {event.place ? ` · ${event.place}` : ""}
                    <br />
                    {eventWhen(event.startsOn, event.endsOn)}
                  </p>
                  <Badge variant="secondary" className="shrink-0">
                    {EVENT_STATUS_LABELS[event.status]}
                  </Badge>
                </div>
                <div className="flex items-baseline justify-between gap-3 border-t pt-3">
                  <span className="text-muted-foreground text-xs">ກຳໄລ</span>
                  <span className="font-display text-2xl tabular-nums">
                    {formatLak(event.profit)}
                  </span>
                </div>
                {canWrite ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {event.status !== "open" ? (
                      <Button
                        type="button"
                        className="h-12 flex-1 text-base"
                        disabled={openEvent.isPending}
                        onClick={() => void openSale()}
                      >
                        {event.status === "closed" ? "ເປີດຂາຍອີກ" : "ເປີດຂາຍ"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 flex-1"
                        disabled={closeEvent.isPending}
                        onClick={() => void closeSale()}
                      >
                        ປິດງານ
                      </Button>
                    )}
                    {event.status === "draft" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-12"
                        disabled={removeEvent.isPending}
                        onClick={() => void deleteDraft()}
                      >
                        ລຶບງານ
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex min-h-12 w-full justify-start overflow-x-auto">
                  <TabsTrigger className="shrink-0" value="sell">
                    ຂາຍ
                  </TabsTrigger>
                  <TabsTrigger className="shrink-0" value="items">
                    ສິນຄ້າ
                  </TabsTrigger>
                  <TabsTrigger className="shrink-0" value="stock">
                    ເບີກ
                  </TabsTrigger>
                  <TabsTrigger className="shrink-0" value="expenses">
                    ຈ່າຍ
                  </TabsTrigger>
                  <TabsTrigger className="shrink-0" value="summary">
                    ສະຫຼຸບ
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="sell">
                  {event.status === "open" ? (
                    <EventSaleSellPanel eventId={id} canWrite={canWrite} />
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {event.status === "draft"
                        ? "ເປີດຂາຍກ່ອນບັນທຶກບິນ"
                        : "ງານປິດແລ້ວ ຂາຍຕໍ່ບໍ່ໄດ້ຈົນກວ່າຈະເປີດໃໝ່"}
                    </p>
                  )}
                </TabsContent>
                <TabsContent value="items">
                  <EventSaleCatalogPanel eventId={id} canWrite={editable} />
                </TabsContent>
                <TabsContent value="stock">
                  <EventSaleStockPanel eventId={id} canWrite={editable} />
                </TabsContent>
                <TabsContent value="expenses">
                  <EventSaleExpensePanel eventId={id} canWrite={editable} />
                </TabsContent>
                <TabsContent value="summary">
                  <EventSaleSummaryPanel eventId={id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </Main>
    </>
  );
}
